import { RefreshCw } from "lucide-react";
import { Button, DataTable, Drawer } from "@/shared/rd";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useWhatsappMessage, useWhatsappQuery } from "../services/useWhatsapp";
import {
  languageDisplayName,
  metaDeliveryStatus,
  templateDisplayName,
} from "../services/value-format";
import {
  CoverageNote,
  NoRecords,
  Pager,
  QueryState,
  StatusBadge,
  Timestamp,
} from "./Shared";
import { Detail, EventDetails, HistoryStatus } from "./MessagesPanel";
import { kvStyle, listStyle, muted, rowButton, sectionHeading, summaryStyle } from "./styles";
const copy = keys.whatsapp;

function AttemptDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const t = useTranslations();
  const locale = useLocale();
  const result = useWhatsappMessage(id);
  const data = result.data;
  return (
    <Drawer
      open
      onClose={onClose}
      crumb={`${t(copy.nav)} / ${t(copy.details)}`}
      badge="chat"
      width={540}
      title={data ? templateDisplayName(data.templateName, t) : t(copy.details)}
      sub={data ? <bdi dir="ltr">{data.recipientMasked}</bdi> : undefined}
      closeLabel={t(copy.close)}
    >
      <QueryState
        loading={result.isLoading}
        error={result.error}
        retry={result.refetch}
        page={t(copy.details).toLocaleLowerCase(locale)}
      >
        {data && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
            <p style={muted}>{t(copy.attemptsSourceHint)}</p>
            <dl className="rd-kv" style={kvStyle}>
              <Detail label={t(copy.seller)}>
                <bdi>
                  {data.sellerName ?? data.sellerId ?? t(copy.unattributed)}
                </bdi>
              </Detail>
              <Detail label={t(copy.store)}>
                <bdi>
                  {data.storeName ?? data.storeId ?? t(copy.unattributedStore)}
                </bdi>
              </Detail>
              <Detail label={t(copy.purpose)}>
                {t(copy.purposes[data.purpose])}
              </Detail>
              <Detail label={t(copy.template)}>
                <bdi>{data.templateName}</bdi>
              </Detail>
              <Detail label={t(copy.language)}>
                <bdi>
                  {languageDisplayName(data.languageCode, locale)} (
                  {data.languageCode})
                </bdi>
              </Detail>
              <Detail label={t(copy.recipient)}>
                <bdi dir="ltr">{data.recipientMasked}</bdi>
              </Detail>
              <Detail label={t(copy.attemptedAt)}>
                <Timestamp value={data.attemptedAt} />
              </Detail>
              <Detail label={t(copy.historyRequestResult)}>
                <StatusBadge value={data.requestOutcome} />
              </Detail>
              <Detail label={t(copy.historyAcceptedAt)}>
                <Timestamp value={data.httpAcceptedAt} />
              </Detail>
              {data.errorCode && !data.failedAt && (
                <Detail label={t(copy.errorCode)}>
                  <bdi>{data.errorCode}</bdi>
                </Detail>
              )}
            </dl>
            {data.requestOutcome === "unknown" && (
              <p style={muted}>{t(copy.requestUnknownHint)}</p>
            )}
            <h3 style={sectionHeading}>{t(copy.webhookSource)}</h3>
            {data.receipts.length ? (
              <ol style={listStyle}>
                {data.receipts.map((receipt, index) => (
                  <EventDetails
                    key={`${receipt.status}-${receipt.occurredAt}-${index}`}
                    event={{ ...receipt, timestamp: receipt.occurredAt }}
                  />
                ))}
              </ol>
            ) : (
              <p style={muted}>{t(copy.noReceipts)}</p>
            )}
            {data.receiptsTruncated && (
              <p style={muted}>{t(copy.metaHistoryTruncated)}</p>
            )}
            <details>
              <summary style={summaryStyle}>
                {t(copy.historyIdentifiers)}
              </summary>
              <dl className="rd-kv" style={{ ...kvStyle, marginTop: 10 }}>
                {[
                  [copy.historyAttemptId, data.id],
                  [copy.providerMessageId, data.providerMessageId],
                  [copy.sender, data.phoneNumberId],
                  [copy.waba, data.businessAccountId],
                  [copy.sellerId, data.sellerId],
                  [copy.storeId, data.storeId],
                ].map(([label, value]) => (
                  <Detail key={label} label={t(label!)}>
                    <bdi dir="ltr">{value ?? t(copy.notAvailable)}</bdi>
                  </Detail>
                ))}
              </dl>
            </details>
          </div>
        )}
      </QueryState>
    </Drawer>
  );
}

export function AttemptsPanel({
  query,
  messageId,
  onMessage,
  onPage,
  onRefresh,
}: {
  query: string;
  messageId: string | null;
  onMessage: (id: string | null) => void;
  onPage: (page: number) => void;
  onRefresh: () => void;
}) {
  const t = useTranslations();
  const result = useWhatsappQuery("messages", query);
  const data = result.data;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
        <p style={{ ...muted, flex: "1 1 320px", color: "var(--rd-text-secondary)" }}>{t(copy.attemptsSourceHint)}</p>
        <Button variant="secondary" size="sm" icon={RefreshCw} loading={result.isFetching} loadingLabel={t("whatsapp.refreshing")} onClick={onRefresh}>
          {t(copy.refresh)}
        </Button>
      </div>
      <QueryState loading={result.isLoading} error={result.error} retry={result.refetch} page={t(copy.triotyMessages)} rows={6}>
        {data && (
          <>
            <CoverageNote coverage={data.coverage} />
            <p style={muted}>{t(copy.submittedHint)}</p>
            <DataTable
              label={t(copy.triotyMessages)}
              rows={data.items}
              rowKey={(row) => row.id}
              minWidth={980}
              empty={
                <NoRecords
                  title={t("whatsapp.attemptsEmptyTitle")}
                  text={t("whatsapp.attemptsEmptyText")}
                  period={{ title: t("whatsapp.periodEmptyTitle"), text: t("whatsapp.periodEmptyText") }}
                />
              }
              onRowClick={(row) => onMessage(row.id)}
              footer={data.total > 0 ? <Pager {...data} onChange={onPage} /> : undefined}
              columns={[
                {
                  key: "template",
                  label: t(copy.template),
                  width: 1.6,
                  render: (row) => (
                    <button
                      type="button"
                      className="rd-cell-stack"
                      style={rowButton}
                      onClick={(event) => {
                        event.stopPropagation();
                        onMessage(row.id);
                      }}
                    >
                      <span className="rd-item-name" style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
                        {templateDisplayName(row.templateName, t)}
                      </span>
                      <span className="rd-cell-sub">
                        {t(copy.purposes[row.purpose])} · <bdi>{row.languageCode}</bdi>
                      </span>
                    </button>
                  ),
                },
                {
                  key: "seller",
                  label: t(copy.seller),
                  width: 1.2,
                  render: (row) => (
                    <div className="rd-cell-stack">
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <bdi>{row.sellerName ?? row.sellerId ?? t(copy.unattributed)}</bdi>
                      </span>
                      <span className="rd-cell-sub">
                        <bdi>{row.storeName ?? row.storeId ?? t(copy.unattributedStore)}</bdi>
                      </span>
                    </div>
                  ),
                },
                {
                  key: "recipient",
                  label: t(copy.recipient),
                  width: 1.1,
                  render: (row) => (
                    <bdi dir="ltr" className="rd-num">
                      {row.recipientMasked}
                    </bdi>
                  ),
                },
                {
                  key: "submitted",
                  label: t(copy.attemptedAt),
                  width: 1.1,
                  render: (row) => (
                    <span className="rd-num" style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>
                      <Timestamp value={row.attemptedAt} />
                    </span>
                  ),
                },
                {
                  key: "outcome",
                  label: t(copy.historyRequestResult),
                  width: 1.2,
                  render: (row) => (
                    <div className="rd-cell-stack">
                      <StatusBadge value={row.requestOutcome} />
                      {row.errorCode && !row.failedAt && (
                        <span className="rd-cell-sub">
                          {t(copy.errorCode)}: <bdi>{row.errorCode}</bdi>
                        </span>
                      )}
                    </div>
                  ),
                },
                {
                  key: "delivery",
                  label: t(copy.metaDeliverySummary),
                  width: 1.1,
                  render: (row) =>
                    metaDeliveryStatus(row) ? (
                      <HistoryStatus status={metaDeliveryStatus(row)!} />
                    ) : (
                      <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>
                        {t(copy.deliveryNotReported)}
                      </span>
                    ),
                },
              ]}
            />
          </>
        )}
      </QueryState>
      {messageId && <AttemptDetail key={messageId} id={messageId} onClose={() => onMessage(null)} />}
    </div>
  );
}
